/**
 * Core Pachisi Game Types
 *
 * All TypeScript types and interfaces for the Pachisi game.
 * Board: cross-shaped with shared track (68 squares), 4 home bases (charkoni/center),
 * 4 home columns (7 squares each), and central home.
 *
 * Key differences from Ludo:
 * - Uses cowrie shells (6) instead of a die
 * - Longer track (68 vs 52)
 * - Grace throws (0, 1, 6 shells up) allow entering and grant extra turns
 * - Blocking: two same-color pawns on a square block opponents from passing
 * - Castle/safe squares marked with X
 */

// ============================================================================
// Player & Game Configuration
// ============================================================================

export type PlayerColor = 'red' | 'blue' | 'green' | 'yellow';
export type PachisiDifficulty = 'easy' | 'medium' | 'hard';
export type PlayerType = 'human' | 'ai';

// ============================================================================
// Cowrie Shell Types
// ============================================================================

/** Result of throwing 6 cowrie shells */
export interface CowrieResult {
  shellsUp: number;       // 0-6: how many shells landed face-up
  moveValue: number;       // mapped move value
  isGrace: boolean;        // true for 0, 1, or 6 shells up
  player: PlayerColor;
}

// ============================================================================
// Pawn Types
// ============================================================================

/** Where a pawn currently resides */
export type PawnLocation = 'home_base' | 'track' | 'home_column' | 'home';

/** Position of a single pawn */
export interface PawnPosition {
  pawnIndex: number;           // 0–3
  location: PawnLocation;
  trackPosition?: number;       // 0–67 absolute position on shared track
  homeColumnPosition?: number;  // 0–6 on home column
}

/** Identifies a specific pawn */
export interface PawnIdentifier {
  player: PlayerColor;
  pawnIndex: number;
}

/** Full info about a pawn including its owner */
export interface PawnInfo {
  player: PlayerColor;
  pawnIndex: number;
  position: PawnPosition;
}

/** Board position for rendering (row/col on the visual grid) */
export interface BoardPosition {
  row: number;
  col: number;
}

// ============================================================================
// Move Types
// ============================================================================

export type MoveAction = 'enter' | 'move' | 'skip';

export interface PachisiMove {
  player: PlayerColor;
  pawnIndex: number;
  action: MoveAction;
  moveValue: number;
}

export interface MoveValidation {
  valid: boolean;
  reason?: 'not_your_turn' | 'invalid_pawn' | 'no_grace_to_enter' | 'blocked_by_own_pawn' | 'blocked_by_opponent_block' | 'exceeds_home' | 'pawn_already_home' | 'game_over';
}

export interface MoveResult {
  success: boolean;
  move: PachisiMove;
  newPosition: PawnPosition;
  captured?: PawnInfo;
  reachedHome: boolean;
  extraTurn: boolean;
  turnSkipped: boolean;
  gameOver: boolean;
  winner?: PlayerColor;
  error?: string;
}

export interface MoveRecord {
  moveNumber: number;
  player: PlayerColor;
  shellsUp: number;
  moveValue: number;
  pawnIndex: number;
  action: MoveAction;
  fromPosition: PawnPosition;
  toPosition: PawnPosition;
  captured?: PawnInfo;
  reachedHome: boolean;
  extraTurn: boolean;
  timestamp: number;
}

// ============================================================================
// Game State
// ============================================================================

export type PachisiGameStatus =
  | { type: 'active' }
  | { type: 'win'; winner: PlayerColor }
  | { type: 'resignation'; winner: PlayerColor; resigned: PlayerColor };

export interface PachisiGameState {
  pawns: Record<PlayerColor, PawnPosition[]>;
  playerCount: number;
  activePlayers: PlayerColor[];
  playerTypes: Record<PlayerColor, PlayerType>;
  currentTurn: PlayerColor;
  lastCowrieResult: CowrieResult | null;
  awaitingMove: boolean;
  pawnsHome: Record<PlayerColor, number>;
  moveHistory: MoveRecord[];
  gameStatus: PachisiGameStatus;
  gameId: string;
  startedAt: number;
  lastMoveAt: number;
}

// ============================================================================
// Game Result & Statistics
// ============================================================================

export interface PachisiGameResult {
  gameId: string;
  mode: PachisiGameSettings['mode'];
  difficulty?: PachisiDifficulty;
  playerCount: number;
  winner?: PlayerColor;
  reason: 'all_pawns_home' | 'resignation' | 'last_player_standing';
  totalMoves: number;
  capturesMade: number;
  graceThrows: number;
  duration: number;
  completedAt: number;
}

export interface PachisiGameStats {
  wins: number;
  losses: number;
  winPercentage: number;
  averageGameLength: number;
  totalCaptures: number;
}

export interface PachisiStatistics {
  userId?: string;
  singlePlayer: {
    easy: PachisiGameStats;
    medium: PachisiGameStats;
    hard: PachisiGameStats;
  };
  localMultiplayer: PachisiGameStats;
  onlineMultiplayer: PachisiGameStats;
  totalGames: number;
  totalPlayTime: number;
}

// ============================================================================
// Game Settings
// ============================================================================

export interface PachisiGameSettings {
  mode: 'singleplayer' | 'local-multiplayer' | 'multiplayer-realtime';
  aiDifficulty?: PachisiDifficulty;
  playerCount: number;        // 2–4
  aiOpponentCount?: number;   // 1–3 (for single-player)
  isPrivate?: boolean;
}

// ============================================================================
// Component Props
// ============================================================================

export interface PachisiGameProps {
  onExit?: () => void;
  initialRoomCode?: string;
}

export interface PachisiGameModeSelectorProps {
  onStartGame: (settings: PachisiGameSettings) => void;
  onJoinPrivateGame?: (roomCode: string) => Promise<void>;
  guestGamesRemaining?: number;
  isAuthenticated?: boolean;
  isOnline?: boolean;
}

// ============================================================================
// Animation & Multiplayer
// ============================================================================

export interface PawnAnimation {
  player: PlayerColor;
  pawnIndex: number;
  fromPosition: BoardPosition;
  toPosition: BoardPosition;
  type: 'move' | 'enter' | 'capture' | 'home';
}

export interface TurnSkipInfo {
  player: PlayerColor;
  reason: 'no_valid_moves';
  nextTurn: PlayerColor;
}

// ============================================================================
// Constants
// ============================================================================

export const TRACK_LENGTH = 68;
export const HOME_COLUMN_LENGTH = 7;
export const PAWNS_PER_PLAYER = 4;
export const NUM_COWRIE_SHELLS = 6;

export const PLAYER_COLORS: PlayerColor[] = ['red', 'blue', 'green', 'yellow'];

/** Starting positions on the shared track (absolute positions 0–67) */
export const PLAYER_START_POSITIONS: Record<PlayerColor, number> = {
  red: 0,
  blue: 17,
  green: 34,
  yellow: 51,
};

/** Home column entry points — the last track position before entering home column */
export const HOME_COLUMN_ENTRY: Record<PlayerColor, number> = {
  red: 66,
  blue: 15,
  green: 32,
  yellow: 49,
};

/**
 * Safe/castle square positions on the shared track (absolute).
 * In Pachisi, castle squares are marked with X — pawns cannot be captured here.
 * Includes start squares + additional castle squares.
 */
export const SAFE_SQUARES: number[] = [
  0, 8, 12, 17, 25, 29, 34, 42, 46, 51, 59, 63,
];

/**
 * Cowrie shell throw mapping:
 * shellsUp → moveValue
 * 0 shells up = 25 (grace)
 * 1 shell up  = 10 (grace)
 * 2 shells up = 2
 * 3 shells up = 3
 * 4 shells up = 4
 * 5 shells up = 5
 * 6 shells up = 6 (grace)
 */
export const COWRIE_VALUES: Record<number, number> = {
  0: 25,
  1: 10,
  2: 2,
  3: 3,
  4: 4,
  5: 5,
  6: 6,
};

/** Grace throws: shells-up values that allow entering pawns and grant extra turns */
export const GRACE_THROWS: number[] = [0, 1, 6];
